import { useParams, useNavigate } from 'react-router-dom'
import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@brent/core-ui/ui'
import { ArrowLeft, Loader2 } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import {
  useQuestion,
  useQuestionVotes,
  useQuestionResults,
  useUpdateQuestion,
  useDeleteQuestion,
  useChangeQuestionStatus,
} from '@/hooks/useQuestions'
import QuestionForm from '@/components/features/questions/QuestionForm'
import QuestionResults from '@/components/features/questions/QuestionResults'
import { useState } from 'react'
import type { CreateQuestionInput } from '@/services/api/questions'

const statusVariants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  draft: 'secondary',
  live: 'default',
  archived: 'outline',
}

export function QuestionDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  const [editOpen, setEditOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)

  const { data: question, isLoading, error } = useQuestion(id!)
  const { data: votes = [] } = useQuestionVotes(id!)
  const { data: results } = useQuestionResults(id!)

  const updateMutation = useUpdateQuestion()
  const deleteMutation = useDeleteQuestion()
  const statusMutation = useChangeQuestionStatus()

  const handleUpdate = async (input: CreateQuestionInput) => {
    try {
      await updateMutation.mutateAsync({ id: id!, input })
      toast({ title: 'Question updated' })
      setEditOpen(false)
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Update failed',
        description: err instanceof Error ? err.message : 'An error occurred',
      })
    }
  }

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(id!)
      toast({ title: 'Question deleted' })
      navigate('/questions')
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Delete failed',
        description: err instanceof Error ? err.message : 'An error occurred',
      })
    }
  }

  const handleStatusChange = async (newStatus: string) => {
    try {
      await statusMutation.mutateAsync({ id: id!, status: newStatus })
      toast({ title: `Question ${newStatus === 'live' ? 'published' : newStatus}` })
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Status change failed',
        description: err instanceof Error ? err.message : 'An error occurred',
      })
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 animate-pulse rounded bg-muted" />
        <div className="h-48 animate-pulse rounded-lg bg-muted" />
      </div>
    )
  }

  if (error || !question) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate('/questions')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Questions
        </Button>
        <Alert variant="destructive">
          <AlertDescription>
            {error instanceof Error ? error.message : 'Question not found'}
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  const isDraft = question.status === 'draft'
  const isLive = question.status === 'live'

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/questions')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        <Badge variant={statusVariants[question.status] ?? 'outline'} className="text-sm">
          {question.status}
        </Badge>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <CardTitle className="text-xl">{question.statement}</CardTitle>
            <div className="flex gap-2 flex-shrink-0">
              {isDraft && (
                <>
                  <Dialog open={editOpen} onOpenChange={setEditOpen}>
                    <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
                      Edit
                    </Button>
                    <DialogContent className="max-w-2xl">
                      <DialogHeader>
                        <DialogTitle>Edit Question</DialogTitle>
                        <DialogDescription>Update the question text and options.</DialogDescription>
                      </DialogHeader>
                      <QuestionForm
                        initialStatement={question.statement}
                        initialOptions={question.options}
                        onSubmit={handleUpdate}
                        isPending={updateMutation.isPending}
                        submitLabel="Save Changes"
                      />
                    </DialogContent>
                  </Dialog>

                  <Button
                    size="sm"
                    onClick={() => handleStatusChange('live')}
                    disabled={statusMutation.isPending}
                  >
                    {statusMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Publish
                  </Button>

                  <Button variant="destructive" size="sm" onClick={() => setDeleteOpen(true)}>
                    Delete
                  </Button>
                </>
              )}

              {isLive && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleStatusChange('draft')}
                    disabled={statusMutation.isPending}
                  >
                    Unpublish
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleStatusChange('archived')}
                    disabled={statusMutation.isPending}
                  >
                    Archive
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {question.options.map((option) => (
              <div key={option.id} className="flex items-center gap-3 rounded-md border p-3">
                <span className="text-sm font-medium text-muted-foreground">{option.id})</span>
                <span>{option.text}</span>
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs text-muted-foreground">
            Created {new Date(question.created_at).toLocaleString()}
          </p>
        </CardContent>
      </Card>

      {results && <QuestionResults options={question.options} results={results} votes={votes} />}

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this question?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The question and all its data will be permanently
              deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleteMutation.isPending}>
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
